import React, { useEffect } from 'react';

import { usePreFormWizard } from '@/hooks/submission_forms/usePreFormWizard';
import { useSubCategoryAutoSelect } from '@/hooks/submission_forms/useSubCategoryAutoSelect';
import FullPageHeader from '@/components/FullPageHeader';
import LoadingState from '@/components/wizard/LoadingState';
import ErrorState from '@/components/wizard/ErrorState';
import WizardStep from '@/components/wizard/WizardStep';
import ReviewStep from '@/components/wizard/ReviewStep';
import { WizardStepType } from '@/components/wizard/types';
import { useNavigate, useSearchParams } from 'react-router-dom';
import OrphanSiteWarningBanner from '@/features/submission_forms/components/OrphanSiteWarningBanner';

const PreFormWizard: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const ownerId = searchParams.get('owner_id');

  const onClose = () => {
    navigate(-1);
  };

  const {
    currentStepData,
    isLoading,
    error,
    selection,
    owner,
    blockedMessage,
    hasNoCategories,

    // Actions
    selectOption,
    nextStep,
    previousStep,
    reset,
    initializeForm,
  } = usePreFormWizard();

  // Back from a later step clears the sub-category without touching the
  // category, so this is what re-arms an auto-select for a second visit.
  const hasSubCategorySelection = (selection.subCategory ?? []).length > 0;

  // When the user picks "New Site/Investigator" as their category and the owner
  // is a Study with a known EAP status (owner.eap_status, computed server-side —
  // see SubmissionPreFormBuilder#owner_info), skip the redundant "Is this an EAP
  // site?" question by auto-selecting it here, the same derivation
  // PreFormWizardWithSubCategory uses on the dedicated /sites/new?owner_id= route.
  // This is the only in-app path that currently reaches a New Site/Investigator
  // submission from a Study's "Add Submission" button, so the auto-select has to
  // live here too — not just on the separate, unlinked /sites/new wizard.
  // Unlike PreFormWizardWithSubCategory (fixed category prop), here the user
  // picks category themselves and can change it, so reArmOnCategoryChange is
  // true — a pick away from the target category resets the seed so a later
  // re-pick is evaluated again instead of staying permanently skipped/stuck.
  const { subCategoryAutoSelected: eapAutoSelected } = useSubCategoryAutoSelect(
    {
      categorySeeded: true,
      currentCategory: selection.category ?? '',
      currentStepData,
      targetCategory: 'New Site/Investigator',
      derivedSubCategory: owner?.eap_status,
      hasSubCategorySelection,
      selectOption,
      reArmOnCategoryChange: true,
    }
  );

  // OV-1358: the same treatment for Continuing Review. Which of the three CR
  // forms applies is fully determined by what the user is filing FROM — a site,
  // a single-site study or a multi-site study — so asking is redundant and is a
  // chance to file the wrong one. The backend derives it
  // (owner.continuing_review_sub_category); when it can't (a study whose type
  // was never recorded, common on transferred studies) the value is absent and
  // the step is shown as before.
  //
  // Safe to run alongside the EAP instance above: each is inert unless its own
  // targetCategory is the selected one, so at most one ever fires.
  const { subCategoryAutoSelected: continuingReviewAutoSelected } =
    useSubCategoryAutoSelect({
      categorySeeded: true,
      currentCategory: selection.category ?? '',
      currentStepData,
      targetCategory: 'Continuing Review',
      derivedSubCategory: owner?.continuing_review_sub_category,
      hasSubCategorySelection,
      selectOption,
      reArmOnCategoryChange: true,
    });

  const subCategoryAutoSelected =
    eapAutoSelected || continuingReviewAutoSelected;

  // previousStep takes an optional lockedSteps: WizardStepType[] argument —
  // passing it directly as a Button's onClick would leak the click event in
  // as that argument (event.includes is not a function). Whenever the
  // subcategory was derived rather than chosen — EAP status for a nested site,
  // or the CR form implied by the owner's shape — it isn't a real decision, so
  // re-showing it on Back isn't useful and it's locked the same way
  // PreFormWizardWithSubCategory locks it on the dedicated /sites/new?owner_id=
  // route: previousStep skips straight over it to Category instead of stopping
  // there.
  const handleBack = () => {
    previousStep(subCategoryAutoSelected ? [WizardStepType.SUBCATEGORY] : []);
  };

  useEffect(() => {
    initializeForm(ownerId || undefined);
  }, [initializeForm, ownerId]);

  const handleRetry = () => {
    initializeForm(ownerId || undefined);
    reset();
  };

  // Loading state
  if (isLoading) {
    return <LoadingState message="Loading form options..." />;
  }

  // Error state
  if (error) {
    return <ErrorState message={error} onRetry={handleRetry} />;
  }

  // Owner is not currently eligible for any submission type
  if (hasNoCategories) {
    return (
      <ErrorState
        variant="info"
        message={
          blockedMessage ??
          'There are no submission types available for this item right now.'
        }
        onBack={onClose}
        actionLabel="Go Back"
      />
    );
  }

  // Main wizard flow

  // Once "New Site/Investigator" is picked with no owner (i.e. no parent Study),
  // the resulting site would be orphaned — nudge toward creating it from the
  // Study instead, without blocking progress. Mirrors the same banner shown on
  // the dedicated /sites/new wizard.
  const showOrphanSiteWarning =
    !ownerId && selection.category === 'New Site/Investigator';

  // Hides the subcategory pill/breadcrumb once it's been auto-selected — the
  // user never actually answered it, so it shouldn't appear as one of "their"
  // answers. Navigation itself is separately locked via handleBack's
  // lockedSteps argument.
  const hiddenSteps = subCategoryAutoSelected
    ? [WizardStepType.SUBCATEGORY]
    : [];

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <FullPageHeader title="New Submission" onClose={onClose} />

      {/* FullPageHeader is fixed, so it sits outside the flow — pt-[72px] reserves its
          height the same way SubmissionForm does. Without it, justify-center pushes the
          top of a tall card (the multi-select sub-category step is 12 options plus
          helper text) up underneath the header, clipping the study name. */}
      <div className="flex-1 flex flex-col justify-center py-8 pt-[72px]">
        {showOrphanSiteWarning && (
          <div className="w-6xl mx-auto px-8">
            <OrphanSiteWarningBanner />
          </div>
        )}
        {currentStepData.currentStep === WizardStepType.REVIEW ? (
          <ReviewStep
            selection={selection}
            owner={owner}
            hiddenSteps={hiddenSteps}
            onBack={handleBack}
            onReset={reset}
          />
        ) : (
          <WizardStep
            currentStepData={currentStepData}
            selection={selection}
            owner={owner}
            hiddenSteps={hiddenSteps}
            // Actions
            onSelect={selectOption}
            onBack={handleBack}
            // Only a multi-select step renders a Continue button (OV-1180);
            // single-select steps advance on click.
            onNext={() => nextStep()}
            onReset={reset}
          />
        )}
      </div>
    </div>
  );
};

export default PreFormWizard;
